import { isDev } from '../../../../core/utils/is-node-environment.js';
import { isDataTableV2GroupColumnDef } from '../../hooks/useColumns.js';
import type {
  DataTableV2ColumnDef,
  DataTableV2GroupColumnDef,
} from '../../public.api.js';

/**
 * Extracts the column order as specified in a column definition.
 * Also saves all encounterd column groups an array.
 *
 * @param columns - column definition
 * @returns extracted column order and column groups
 */
export function extractColumnOrderAndGroups<T>(
  columns: DataTableV2ColumnDef<T>[],
): {
  columnOrder: string[];
  columnGroups: string[][];
} {
  const columnGroups: string[][] = [];
  const columnOrder = columns.flatMap((column: DataTableV2ColumnDef<T>) => {
    if (isDataTableV2GroupColumnDef(column)) {
      const leafColumnsIds = (
        column as DataTableV2GroupColumnDef<T, unknown>
      ).columns?.map((column) => column.id);
      leafColumnsIds && columnGroups?.push(leafColumnsIds);
      return leafColumnsIds || [];
    }
    return column.id;
  });
  return { columnOrder, columnGroups };
}

/**
 * Validates a passed column order. Removes duplicated and invalid ids, adds missing ids
 * and ensures child columns with the same parent are placed next to each other.
 *
 * @param columnOrder - column order to validate
 * @param columnOrderFromDefinition - column order as defined in the column definition (valid)
 * @param columnGroups - array of all column groups, each group is an arry of ids
 * @returns corrected, valid column order or null if the order was already valid
 */
export function validateColumnOrder(
  columnOrder: string[],
  columnOrderFromDefinition: string[],
  columnGroups: string[][],
): string[] | null {
  const _columnOrderFromDefinition = [...columnOrderFromDefinition];
  const _columnGroups = [...columnGroups];
  let validColumnOrder = [...columnOrder];
  let isInvalid = false;

  // check for duplicate ids and remove them
  const columnOrderDuplicatesRemoved = Array.from(new Set(columnOrder));

  const defaultColumnOrderSet = new Set(_columnOrderFromDefinition);
  const columnOrderSet = new Set(columnOrderDuplicatesRemoved);

  if (validColumnOrder.length !== columnOrderDuplicatesRemoved.length) {
    isInvalid = true;
    validColumnOrder = columnOrderDuplicatesRemoved;

    if (isDev()) {
      // eslint-disable-next-line no-console
      console.warn(
        'The specified column order contains duplicate values. These have been removed.',
      );
    }
  }

  // check for invalid ids that don't exist in the column definition and remove them
  validColumnOrder = validColumnOrder.filter((columnId) =>
    defaultColumnOrderSet.has(columnId),
  );

  if (validColumnOrder.length !== columnOrderDuplicatesRemoved.length) {
    isInvalid = true;
    if (isDev()) {
      // eslint-disable-next-line no-console
      console.warn(
        "The specified column order contains ids that don't exist in the column definition. These have been removed.",
      );
    }
  }

  // check for column ids that are missing in the column order and add them
  const missingIds = _columnOrderFromDefinition.filter(
    (columnId) => !columnOrderSet.has(columnId),
  );
  validColumnOrder.push(...missingIds);

  if (missingIds.length > 0) {
    isInvalid = true;
    if (isDev()) {
      // eslint-disable-next-line no-console
      console.warn(
        'The specified column order does not contain all column ids. Missing columns have been appended at the end.',
      );
    }
  }

  // ensure columns with the same parent are not separated in the column order
  _columnGroups.forEach(([...siblingColumnIds]) => {
    // find the index of the first occurence of a sibling's id in the valid column order
    const firstChildIndex = validColumnOrder.findIndex((columnId) => {
      const columnIndex = siblingColumnIds.indexOf(columnId);
      if (columnIndex !== -1) {
        // remove first child from sibling array as it will remain in this position
        siblingColumnIds.splice(columnIndex, 1);
        return true;
      }
      return false;
    });

    const areSiblingsAdjacent = [...validColumnOrder]
      .splice(firstChildIndex + 1, siblingColumnIds.length)
      .every((columnId) => {
        return siblingColumnIds.includes(columnId);
      });

    if (!areSiblingsAdjacent) {
      isInvalid = true;
      if (isDev()) {
        // eslint-disable-next-line no-console
        console.warn(
          'The specified column order is invalid, child columns with the same parent must be next to each other. The order has been corrected.',
        );
      }

      const extractedColumnIds: string[] = [];
      // extract all sibling column ids except the first one
      validColumnOrder = validColumnOrder.filter((columnId) => {
        if (siblingColumnIds.includes(columnId)) {
          extractedColumnIds.push(columnId);
          return false;
        }
        return true;
      });

      validColumnOrder.splice(firstChildIndex + 1, 0, ...extractedColumnIds);
    }
  });

  return isInvalid ? validColumnOrder : null;
}
